import axios from "axios";
import { config_UPTIME } from "../../Constants";
import { getItem } from "../../utils/DataProtection";

const unwrapCollection = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  if (Array.isArray(payload.result)) {
    return payload.result;
  }

  if (Array.isArray(payload.data)) {
    return payload.data;
  }

  return [];
};

const getAuthorizationHeader = () => {
  try {
    const userToken = getItem("user");
    return userToken ? { Authorization: `Bearer ${JSON.parse(userToken)}` } : {};
  } catch (error) {
    return {};
  }
};

const getCustomersApiBaseUrl = () => {
  const baseUrl = config_UPTIME?.url?.API_URL ?? "";

  if (baseUrl.includes("://be.service.")) {
    return baseUrl.replace("://be.service.", "://web.service.");
  }

  if (baseUrl.includes("://uptime.service.")) {
    return baseUrl.replace("://uptime.service.", "://web.service.");
  }

  return baseUrl;
};

const getStoredLegalEntityNodeId = () => {
  try {
    const storedValue = getItem("legalentitynodeid");
    return storedValue ? JSON.parse(storedValue) : "";
  } catch (error) {
    return "";
  }
};

const countMachinesFromCustomer = (customer) => {
  if (Array.isArray(customer?.hasequipment)) {
    return customer.hasequipment.length;
  }

  return null;
};

const countSitesFromCustomer = (customer) => {
  if (Array.isArray(customer?.hassite)) {
    return customer.hassite.length;
  }

  return null;
};

const getPortalStatus = (customer) => {
  if (typeof customer?.portalstatus === "string") {
    return customer.portalstatus;
  }

  if (typeof customer?.customerportalstatus === "string") {
    return customer.customerportalstatus;
  }

  if (typeof customer?.customerportalpublished === "boolean") {
    return customer.customerportalpublished ? "veröffentlicht" : "nicht eingerichtet";
  }

  if (typeof customer?.published === "boolean") {
    return customer.published ? "veröffentlicht" : "nicht eingerichtet";
  }

  return "";
};

const isCustomerActive = (customer) => {
  if (typeof customer?.active === "boolean") {
    return customer.active;
  }

  if (typeof customer?.isactive === "boolean") {
    return customer.isactive;
  }

  if (typeof customer?.status === "number") {
    return customer.status === 1;
  }

  return true;
};

const createCustomerRow = (customer, index) => ({
  id: customer?.nodeid ?? customer?.nodeId ?? `customer-${index}`,
  nodeid: customer?.nodeid ?? customer?.nodeId ?? "",
  name: customer?.name ?? "",
  portal: getPortalStatus(customer),
  locations: countSitesFromCustomer(customer),
  machines: countMachinesFromCustomer(customer),
  active: isCustomerActive(customer),
  raw: customer,
});

const normalizeCustomers = (payload) => {
  const collection = unwrapCollection(payload);

  return collection
    .flatMap((entry) => (Array.isArray(entry?.data) ? entry.data : []))
    .flatMap((dataEntry) => {
      const hasCustomer = dataEntry?.legalentity?.hascustomer;

      if (!Array.isArray(hasCustomer)) {
        return [];
      }

      return hasCustomer.map((customerEntry, index) =>
        createCustomerRow(customerEntry?.legalentity ?? customerEntry, index)
      );
    })
    .filter((row) => row.name);
};

export const fetchCustomers = async () => {
  const legalentitynodeid = getStoredLegalEntityNodeId();

  const response = await axios({
    method: "get",
    url: `${getCustomersApiBaseUrl()}/customers`,
    params: legalentitynodeid ? { legalentitynodeid } : {},
    headers: {
      "Content-Type": "application/json",
      ...getAuthorizationHeader(),
    },
  });

  return normalizeCustomers(response.data);
};
